import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { DatePipe, JsonPipe, NgIf } from "@angular/common";
import { ClrDatagridModule, ClrModalModule, ClrTabsModule, ClrTooltipModule } from "@clr/angular";
import { getTenantLetterAssets } from '../../../_shared/utils/letter-assets';
import { getTenantConfig } from '../../../_shared/utils/tenant-context';
import {
  AlertService,
  DeliveryStoreService,
  FileStoreService,
  IAlertType, InvoicesStoreService, JobsStoreService,
  StaffStoreService
} from "../../../_shared/services";
import {Delivery, DeliveryList, SingleDelivery, StatusButtons, WebserviceResponse} from "../../../_shared/interfaces";
import {DeliveryDetailsDocumentsComponent} from "./delivery-details-documents/delivery-details-documents.component";
import {
  DeliveryDetailsEditPositionComponent
} from "./delivery-details-edit-position/delivery-details-edit-position.component";
import moment from "moment";

import PackageJson from "../../../../../package.json";
import { environment as env } from "../../../../environments/environment";
import {
  ClarityIcons,
  eyeHideIcon,
  fileIcon,
  infoCircleIcon,
  minusCircleIcon,
  noteIcon,
  pencilIcon
} from "@cds/core/icon";

@Component({
  selector: 'app-delivery-details',
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    ClrModalModule,
    ClrTabsModule,
    ClrDatagridModule,
    ClrTooltipModule,
    DatePipe,
    DeliveryDetailsDocumentsComponent,
    DeliveryDetailsEditPositionComponent,
    JsonPipe
  ],
  templateUrl: './delivery-details.component.html',
  styleUrl: './delivery-details.component.sass'
})
export class DeliveryDetailsComponent {
  deliveries: SingleDelivery[] = [];
  positions: Delivery[] = [];
  selected: DeliveryList[] = [];
  projectId: string = '';
  jobId: number = 0;
  deliveryId: number = 0;
  loading1: boolean = true;
  loading2: boolean = true;
  positionId: number = 0;
  partId: number = 0;
  editPos: number = 0;  // 0 = Anzeige; 1 = Änderungsmodus, 2 = Position ändern
  statusLabel = 'label-light-blue';
  statusText = '';
  staffFirstName: string = '';
  staffLastName: string = '';
  staffEMail: string = '';
  staffPhone: string = '';
  disableEditButton: boolean = false; // disbale button
  disableInvoiceButton: boolean = false; // disable button
  disableNoInvoiceButton: boolean = false; // disable button
  disableDeliveryButton: boolean = false; // disable button
  useRemarks: boolean = true;

  public ppsVersion: string = PackageJson.version;

  // Die Reihenfolge ist Schnittstelle, nicht Geschmack: die Knoepfe im Template
  // adressieren ueber den Index. changeDeliveryStatus(..., 1) ist "Lieferschein
  // ohne Rechnung", bookDelivery(..., 2) ist "Lieferschein buchen".
  //
  // Index 1 stand frueher ebenfalls auf 'closed' -- damit schrieb "ohne
  // Rechnung" denselben Status wie "buchen", und der Unterschied war im neuen
  // System nicht mehr erkennbar. Vor der Migration erzeugte dieselbe Handlung
  // 'noinvoice'.
  statusButtons: StatusButtons[] = [
    { state: 'open',      display: 'Offen',         label: 'label-info-blue', icon: 'info-circle'},
    { state: 'noinvoice', display: 'Ohne Rechnung', label: 'label-warning',   icon: 'exclamation-triangle'},
    { state: 'closed',    display: 'Gebucht',       label: 'label-success',   icon: 'check-circle'}
  ];

  // Für Ausdruck
  basic = false;
  clrModalSize: string = '';
  devMode: boolean;
  private readonly tenantConfig = getTenantConfig();

  documentDefinition = getTenantLetterAssets().a4Letter;

  constructor(
    private delss: DeliveryStoreService,
    private staffss: StaffStoreService,
    private invoicess: InvoicesStoreService,
    private jobss: JobsStoreService,
    private filess: FileStoreService,
    private alertDispatcher: AlertService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.devMode = env.stages.dev;
    ClarityIcons.addIcons(noteIcon, eyeHideIcon, fileIcon, infoCircleIcon, pencilIcon, minusCircleIcon)
  }

  ngOnInit() {
    const params = this.route.snapshot.paramMap;
    this.projectId = params.get('projid') ?? '';
    this.jobId = parseInt(params.get('jobid') ?? '0', 10);
    this.deliveryId = parseInt(params.get('delid') ?? '0', 10);
    this.clrModalSize = 'xl';
    this.loadSingleDelivery(this.projectId, this.deliveryId);
    this.loadDeliveriesPositions(this.projectId, this.deliveryId);
  }

  updateDelivery(done: boolean) {
    this.editPos = 1;
    this.loadSingleDelivery(this.projectId, this.deliveryId);
    this.loadDeliveriesPositions(this.projectId, this.deliveryId);
  }

  updatePositionMode(index: number, positionId: number, partId: number) {
    this.editPos = 2;
    this.positionId = positionId;
    this.partId = partId;
  }

  createDeliveryNote(positions : any[]) {
    let benennung = '';
    let serialkostenstelle = '';
    let serial_number = '';
    let controlling_number = '';
    let job = this.deliveries[0].job_id ?? 0;
    let company = this.deliveries[0].company_name ?? '';
    if (this.deliveries[0].department) company += '\n' + this.deliveries[0].department;
    let posTable = [[{ text: 'Pos.'}, { text: 'Bezeichnung/Kennzeichnung'}, { text: 'Zeichnung', colSpan: 1, style: '' }, { text: ''}, { text: 'Material'}, {text: 'Charge'}, { text: 'Anzahl'}]];

    positions.forEach(position => {
      serialkostenstelle = '';
      serial_number = '';
      controlling_number = '';
      benennung = position.name;
      switch (position.serial_nr) {
        case null: serial_number = '';
          break;
        case '': serial_number = '';
          break;
        case 'null': serial_number = '';
          break;
        default: serial_number = position.serial_nr;
          break;
      }
      switch (position.controlling_nr) {
        case null: controlling_number = '';
          break;
        case '': controlling_number = '';
          break;
        case 'null': controlling_number = '';
          break;
        default: controlling_number = position.controlling_nr;
          break;
      }
      if (serial_number.length > 0) serialkostenstelle = "Kennzeichnung: " + serial_number;
      if (controlling_number.length  > 0) serialkostenstelle = "Kostenstelle: " + controlling_number;
      if ((serial_number.length > 0) && (controlling_number.length > 0)) serialkostenstelle = "Kennzeichnung: " + serial_number + "\nKostenstelle: " + controlling_number;
      if (position.taric && position.taric !== "0") benennung += '\n(Zolltarifnummer: ' + position.taric + ')';
      if (position.heat_nr == 'null') position.heat_nr = '';


      switch(position.part) {
        case 0: {
          if (position.technology) {
            posTable.push(
              [ position.position, benennung, position.technical_drawing,  '', position.material, position.heat_nr, '' ]
            );
          } else {
            posTable.push(
              [ position.position, benennung, position.technical_drawing,  '', position.material, position.heat_nr, position.number_of_pieces  ]
            );
          }
          break;
        }
        default: {
          if (position.technology) {
            posTable.push(
              [ position.position + '.' + position.part, benennung, position.technical_drawing,  '', position.material, position.heat_nr, ''  ]
            );
          } else {
            posTable.push(
              [ position.position + '.' + position.part, benennung, position.technical_drawing,  '', position.material, position.heat_nr, position.number_of_pieces  ]
            );
          }
          break;
        }
      }
      if (serialkostenstelle) {
        posTable.push(
          [ { text: "" }, { text: serialkostenstelle, colSpan: 3, style: 'small' }, { text: "" }, { text: "" }, { text: "" }, { text: "" }, { text: "" } ]
        )}
      if (this.useRemarks && position.remarks && position.remarks != 'null') {
        posTable.push(
          [ { text: "" }, { text: "" }, { text: position.remarks, colSpan: 4, style: 'small' }, { text: "" }, { text: "" }, { text: "" }, { text: "" } ]
        )}
    })

    this.documentDefinition.info.title = 'PPS Lieferschein';
    this.documentDefinition.info.subject = 'LIEFERSCHEIN ' + this.projectId + '.0004.' + this.deliveryId;
    this.documentDefinition.info.revison = this.ppsVersion;


    (this.documentDefinition.content[0] as any).columns[0].text = company + '\n' + this.deliveries[0].street + '\n' + this.deliveries[0].postcode + ' ' + this.deliveries[0].city;
    if(this.deliveries[0].country && (this.deliveries[0].country != 'Deutschland')) {
      (this.documentDefinition.content[0] as any).columns[0].text += '\n' + this.deliveries[0].country
    }
    (this.documentDefinition.content[0] as any).columns[2].text = this.tenantConfig.letterCity + ', ' + moment(this.deliveries[0].delivery_date).format("DD.MM.yyyy") + '\n\nIhr Ansprechpartner:\n' + this.staffFirstName + ' ' + this.staffLastName + '\n' + this.staffEMail;
    this.documentDefinition.content[1].text = 'LIEFERSCHEIN ' + this.projectId + '.0004.' + this.deliveryId ;
    this.documentDefinition.content[2].text = 'gemäß unserem Job ' + this.projectId + '.0006.' + job + ' laut Kundenauftrag: ' + this.deliveries[0].order_id ;

    this.documentDefinition.content[3].text = '';

    (this.documentDefinition.content[4] as any).table.headerRows = 1;
    (this.documentDefinition.content[4] as any).table.body = posTable;

    this.documentDefinition.content[5] = {
      stack: [
        { text: '10 Tage nach Lieferdatum werden von uns keine Reklamationen zur Warenlieferung, Stückzahl und Zustand mehr anerkannt!', style: 'fett', margin: [30, 10] },
        {
          layout: 'Borders',
          table: {
            // headers are automatically repeated if the table spans over multiple pages
            // you can declare how many rows should be treated as headers
            headerRows: 1,
            widths: [ '50%', '50%' ],

            body: [
              [ { text: 'Warenempfangsbestätigung/Speditionsübergabebestätigung', style: 'fett', colSpan: 2, margin: [0, 4]},'' ],
              [ [ { text: 'Name der übernehmenden Firma:'},{ text: '(Wareneingangsstempel)', style: 'small'} ], '' ],
              [ [ { text: 'Fahrzeugkennzeichen:'},{ text: '(bei Fremdspedition)', style: 'small'} ], '' ],
              [ [ { text: 'Datum:', margin: [0, 4] } ], '' ],
              [ [ { text: 'Name in Druckschrift:', margin: [0, 4] } ], '' ],
              [ [ { text: 'Unterschrift:', margin: [0, 4] } ], '' ]
            ]
          },
          style: 'normal',
          margin: [30, 10]
        },
        { text: 'Mit der Unterschrift wird die ordnungsgemäße Ladungssicherung anerkannt.', style: 'normal', margin: [30, 10] }
      ]
    } as any;

    this.documentDefinition.content.splice(6);

    this.generatePdf();
  }

  generatePdf() {
    const documentGenerator = pdfMake.createPdf(this.documentDefinition as any);

    documentGenerator.getDataUrl((dataUrl: string) => {
      const targetElement = document.querySelector('#documentContainer');
      targetElement!.innerHTML = '';

      if (!targetElement) {
        this.alertDispatcher.alertConfig.message = 'PDF-Container nicht gefunden!';
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
        return; // Exit the function if the element doesn't exist
      }

      const pdfIframe = document.createElement('iframe');
      pdfIframe.src = dataUrl;
      pdfIframe.width = '100%';
      pdfIframe.height = '100%';
      targetElement.appendChild(pdfIframe);
    })
  }

  deleteDelivery(projectId: string, deliveryId: number, jobId: number, status: string) {
    if (confirm('Wollen Sie diesen Lieferschein wirklich löschen?\nDiese Aktion kann nicht rückgängig gemacht werden!')) {
      this.delss.deleteDeliveryNote(projectId, deliveryId)
        .subscribe({
          next: () => {
            this.router.navigate(['/job/details/', projectId, jobId])
          },
          complete: () => {
            if (status == 'Gebucht') { this.resetDelivery(); }
          }
        });
    }
  }

  resetDelivery() {
    this.jobss.substractPiecesDelivered(this.jobId, this.positions).subscribe({
      next: () => {
        this.alertDispatcher.alertConfig.message = 'Gelieferte Positionen wurden zurückgesetzt!';
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;
      },
      complete: () => {
        this.disableEditButton = false;
        this.disableDeliveryButton = false;
        this.disableNoInvoiceButton = false;
      },
      error: () => {
        return
      }
    })
  }

  loadSingleDelivery(projectId: string, deliveryId: number) {
    this.delss.getSingleDelivery(projectId, deliveryId, 'all')
      .subscribe({
        next: (data1) => {
          if (!data1.hasErrors) {
            this.deliveries = data1.object;
          } else {
            this.alertDispatcher.show({
              message: 'Keine Deliverydaten gefunden! Fehler: ' + data1.hasErrors,
              clrAlertType: IAlertType.WARNING
            }, 5000);
            return;
          }
          // Mitarbeiter personalisieren
          this.staffStatus(this.deliveries[0].employee_phm);
          if (this.deliveryId == 0) this.deliveryId = this.deliveries[0].delivery_id ?? 0;

          // Status gestalten
          const statusline = this.findButtonStatus(this.deliveries[0].status, this.statusButtons);
          this.statusLabel = this.statusButtons[statusline].label;
          this.statusText = this.statusButtons[statusline].display;

          // 'noinvoice' ist ebenso abgeschlossen wie 'closed' -- nur eben ohne
          // Rechnung. Ohne diesen Zweig liessen sich beide Knoepfe erneut
          // druecken und der Status im Nachhinein ueberschreiben.
          if (this.deliveries[0].status === 'closed'
              || this.deliveries[0].status === 'noinvoice') {
            this.disableDeliveryButton = true;
            this.disableNoInvoiceButton = true;
          }
          this.loading1 = false;
        },
        complete: () => {
          // Check, ob Rechnung zum Lieferschein vorhanden
          this.loadInvoice();

        },
        error: () => {
          return
        }
      });
  }

  loadDeliveriesPositions(projectId: string, deliveryID: number) {
    this.delss.getDeliveryPositions(projectId, 0, deliveryID, '0', '0').subscribe({
      next: data2 => {
        this.positions = data2.object;
      },
      complete: () => {
        this.loading2 = false;
      },
      error: () => {
        return
      }
    })
  }

  loadInvoice() {
    let result: number = 0;
    let invoiceNumber: string;
    this.invoicess.getInvoices(0, 0, this.projectId, this.jobId, this.deliveryId,'all')
      .subscribe({
        next: res => {
          result = res.object.length;
          if (result > 0 ) { invoiceNumber = res.object[0].praefix + '.' + res.object[0].number; }
        },
        complete: () => {
          if (result > 0) {
            this.disableInvoiceButton = true;

            this.alertDispatcher.alertConfig.message = 'Zu diesem Lieferschein wurde bereits eine Rechnung erzeugt (' + invoiceNumber + ')!';
            this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;
          }
        },
        error: () => {
          return
        }
      });
  }

  staffStatus(staffId: number){
    this.staffss.getStaffById(staffId)
      .subscribe({
        next: staff => {
          if (!staff) {
            return;
          }
          this.staffFirstName = staff.object[0].firstname;
          this.staffLastName = staff.object[0].lastname;
          this.staffEMail = staff.object[0].email;
          this.staffPhone = staff.object[0].phone;
        },
        complete: () => {
          if (this.devMode) console.log('StaffID ist ' + staffId);
        }
      })
  }

  splitProjectNumber(projectId: string, substring: number) {
    var splits = projectId.split('.');
    return splits[substring];
  }

  // Anpassung Farbe und Text des Statusbuttons...
  findButtonStatus(orderstatus: string, buttons: StatusButtons[]) {
    return buttons.findIndex(el => el.state === orderstatus) ?? 0
  };

  setRemarks(useIt: boolean) {
    this.useRemarks = useIt;
  }

  bookDelivery(projectId: string, deliveryId: number, status: number){
    if (confirm('Wollen Sie den Entwurf einer Rechnung erstellen und den Status dieses Lieferscheins wirklich auf "' + this.statusButtons[status].display + '" setzen?')) {
      this.invoicess.createInvoice(projectId, this.jobId, deliveryId)
        .subscribe({
          next: (invoiceResponse) => {
            const message = invoiceResponse?.message || '';
            const praefix = invoiceResponse?.praefix || '';
            const objectValue = Array.isArray(invoiceResponse?.object)
              ? invoiceResponse.object[0]
              : invoiceResponse?.object;
            const vatMissing =
              objectValue === false ||
              invoiceResponse?.hasErrors === true ||
              message.toLowerCase().includes('invoice_vat_id can not be null or empty');

            if (vatMissing) {
              this.alertDispatcher.show({
                message: `Rechnung erstellen nicht möglich! ${message || 'Bitte UStIDNr. des Rechnungsempfängers prüfen!'}`,
                clrAlertType: IAlertType.DANGER
              }, 7000);
              return;
            }

            this.delss.updateDeliveryStatus(projectId, deliveryId, this.statusButtons[status].state)
              .subscribe({
                next: () => {
                  this.bookingDelivery();
                },
                complete: () => {
                  this.router.navigate(['/invoice/details/', praefix, message]);
                },
                error: (error) => {
                  this.alertDispatcher.show({
                    message: 'Fehler beim Aktualisieren des Lieferstatus: ' + error,
                    clrAlertType: IAlertType.DANGER
                  }, 5000);
                }
              });
          },
          error: (error) => {
            this.alertDispatcher.show({
              message: 'Fehler beim Erstellen der Rechnung: ' + error,
              clrAlertType: IAlertType.DANGER
            }, 5000);
          }
        });
    }
  }

  bookingDelivery() {
    this.jobss.addPiecesDelivered(this.jobId, this.positions).subscribe({
      next: response => {
        this.alertDispatcher.alertConfig.message = 'Gelieferte Positionen wurden aktualisiert!';
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;
      },
      complete: () => {
        this.disableEditButton = true;
        this.disableDeliveryButton = true;
        this.disableNoInvoiceButton = true;
      },
      error: () => {
        return
      }
    })
  }

  changeDeliveryStatus(projectId: string, deliveryId: number, status: number){
    if (confirm('Wollen Sie diesen Lieferschein tatsächlich ohne Rechnung versenden?')) {
      this.delss.updateDeliveryStatus(projectId, deliveryId, this.statusButtons[status].state)
        .subscribe({
          next: data => {
            this.loadSingleDelivery(this.projectId, this.deliveryId);
            this.loadDeliveriesPositions(this.projectId, this.deliveryId);
          },
          complete: () => {
            this.disableDeliveryButton = true;
            this.router.navigate(['/delivery/details/', projectId, this.jobId, deliveryId]);
          },
          error: () => {
            return
          }
        });
    }
  }

  onDelete() {
    let choosenPositions = this.selected;
    if (confirm('Wollen Sie die ausgewählten Positionen tatsächlich löschen?')) {
      choosenPositions.forEach(array => {
        this.deletePosition(this.projectId, this.deliveryId, array.position.toString(), array.part)
      });

      this.loadSingleDelivery(this.projectId, this.deliveryId);
      this.loadDeliveriesPositions(this.projectId, this.deliveryId);

      this.alertDispatcher.alertConfig.message = 'Die Positionen wurden gelöscht.';
      this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
    }
  }

  deletePosition(projectId: string, deliveryId: number, positionId: string, partId: number) {
    this.delss.deleteDeliveryPosition(projectId, deliveryId, positionId, partId)
      .subscribe({
        next: () => {
          this.alertDispatcher.alertConfig.message = 'Die Position(en) wurde(n) gelöscht!';
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;
        },
        complete: () => {
          this.loadDeliveriesPositions(projectId, deliveryId);
        },
        error: () => {
          return
        }
      })
  }

  archivePdf() {
    if (confirm('Wollen Sie die Datei ' + 'Delivery_Note_' + this.projectId + '.0004.' + this.deliveryId + '.pdf archivieren?')) {
      this.safePdf();
    }

    this.alertDispatcher.show({
      message: ' Datei ' + 'Delivery_Note_' + this.projectId + '.0004.' + this.deliveryId + '.pdf wurde archiviert.',
      clrAlertType: IAlertType.SUCCESS
    }, 5000);
  }

  async downloadPdf(){
    if (confirm('Wollen Sie die Datei ' + 'Delivery_Note_' + this.projectId + '.0004.' + this.deliveryId + '.pdf wirklich herunterladen?')) {
      const documentGenerator = pdfMake.createPdf(this.documentDefinition).download('Delivery_Note_' + this.projectId + '.0003.' + this.deliveryId + '.pdf');
    }

    await this.safePdf();

    this.alertDispatcher.alertConfig.message = 'Datei ' + 'Delivery_Note_' + this.projectId + '.0004.' + this.deliveryId + '.pdf wurde herunterladen.';
    this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
  }

  async printPdf() {
    if (confirm('Wollen Sie die Datei ' + 'Delivery_Note_' + this.projectId + '.0004.' + this.deliveryId + '.pdf ausdrucken?')) {
      pdfMake.createPdf(this.documentDefinition).print();
    }

    await this.safePdf()

    this.alertDispatcher.show({
      message: ' Datei ' + 'Delivery_Note_' + this.projectId + '.0004.' + this.deliveryId + '.pdf wurde an den Drucker übergeben. ',
      clrAlertType: IAlertType.INFO
    }, 2000);
  }

  safePdf() {
    const documentGenerator = pdfMake.createPdf(this.documentDefinition as any);

    documentGenerator.getBlob((blob: Blob) => {
      // Convert Blob to File
      const file = new File(
        [blob], // Blob content
        'Delivery_Note_' + this.projectId + '.0004.' + this.deliveryId + '.pdf',
        { type: blob.type, lastModified: Date.now() } // File metadata
      );

      // Now, send the file instead of the Blob
      this.filess.sendFile(file, 5, this.deliveryId, this.projectId, this.jobId, '', 'Lieferschein ' + this.projectId + '.0004.' + this.deliveryId)
        .subscribe({
          next: () =>
          {
            if (this.devMode) console.log("done")
          },
          complete: () => {
            this.alertDispatcher.show({
              message: ' Datei ' + 'Delivery_Note_' + this.projectId + '.0004.' + this.deliveryId + '.pdf wurde gespeichert.',
              clrAlertType: IAlertType.SUCCESS
            }, 5000);
          },
          error: (error) => console.error("Error:", error)
        });
    });
  }

}
